import Loader from "@/components/layout/loader"
import { format } from "date-fns"
import PaymentDetailsItem from "@/payments/payment-details-item"
import NetworkBadge from "@/components/ux/network.badge"
import TokenBadge from "@/components/ux/token.badge"
import { Timeline, TimelineItem } from "@/components/ux/timeline"
import { DetailsSheet } from "@/components/ux/details.sheet"
import { WithdrawalStatus, useWithdrawal } from "@/api/withdrawals"
import { getAccountNumber } from "@/api/bank-accounts"
import { TokenIcon } from "@/components/token"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { InfoCircledIcon } from "@radix-ui/react-icons"

interface WithdrawalDetailsSheetProps {
  id: string;
  onClose: (open: boolean) => void;
}

export function WithdrawalDetailsSheet({
  id,
  onClose,
}: WithdrawalDetailsSheetProps) {
  const { withdrawal } = useWithdrawal(id)

  return (
    <DetailsSheet title="Withdrawal Details" onClose={onClose}>
      {!withdrawal && <Loader message="Loading withdrawal details..." />}
      {withdrawal && (
        <div className="flex flex-col gap-4">
          <div>
            <h1 className="text-xl font-medium">
              {withdrawal.bankAccount.bankName} -{" "}
              {getAccountNumber(withdrawal.bankAccount)}
            </h1>
            <div className="text-sm text-gray-500">
              Withdrawal Requested on{" "}
              {format(withdrawal.createdAt, "MMM do, yyyy hh:ii a")}
            </div>
          </div>
          <div className="flex flex-row gap-2 items-center font-semibold text-lg">
            {withdrawal.token && <TokenIcon name={withdrawal.token} />}
            <div>
              {withdrawal.amount} {withdrawal.token}
            </div>
          </div>
          <div className="flex flex-col py-2 border-t border-b">
            <h2 className="text-lg font-semibold">Timeline</h2>
          </div>
          <Timeline>
            <TimelineItem
              label="Withdrawal Request Received"
              date={withdrawal.createdAt}
            />
            {withdrawal.status === WithdrawalStatus.COMPLETED && (
              <TimelineItem
                label="Withdrawal Processed"
                date={withdrawal.updatedAt}
              />
            )}
            {withdrawal.status === WithdrawalStatus.FAILED && (
              <TimelineItem
                label="Withdrawal Failed"
                date={withdrawal.updatedAt}
                status="failed"
              />
            )}
          </Timeline>
          {withdrawal.status === WithdrawalStatus.COMPLETED && (
            <Alert>
              <InfoCircledIcon className="h-4 w-4" />
              <AlertTitle>Withdrawal Processed</AlertTitle>
              <AlertDescription className="flex flex-col gap-2">
                It may take up to 3 business days for the withdrawal to appear
                in your bank account.
              </AlertDescription>
            </Alert>
          )}
          {withdrawal.status === WithdrawalStatus.FAILED && (
            <Alert variant="destructive">
              <InfoCircledIcon className="h-4 w-4" />
              <AlertTitle>Withdrawal Failed</AlertTitle>
              <AlertDescription className="flex flex-col gap-2">
                We couldn't process your withdrawal. Please contact support for
                more information.
              </AlertDescription>
            </Alert>
          )}
          <div className="py-2 border-b">
            <h2 className="text-lg font-semibold">Withdrawal Details</h2>
          </div>
          <PaymentDetailsItem label="Token">
            <TokenBadge token={withdrawal.token} />
          </PaymentDetailsItem>
          <PaymentDetailsItem label="Network">
            <NetworkBadge network={withdrawal.network} />
          </PaymentDetailsItem>
          <PaymentDetailsItem label="Bank Name">
            {withdrawal.bankAccount.bankName}
          </PaymentDetailsItem>
          <PaymentDetailsItem label="Bank Account">
            {getAccountNumber(withdrawal.bankAccount)}
          </PaymentDetailsItem>
          <PaymentDetailsItem label="Withdrawal Fee">
            {(withdrawal.withdrawalCost + withdrawal.withdrawalFee).toFixed(2)}
          </PaymentDetailsItem>
        </div>
      )}
    </DetailsSheet>
  )
}
